/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * The body for Calendar component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @version 1.2.0
 * @since 1.0.1
 */
import React from 'react';

import { Moment } from 'moment';

import { cellMapper } from './CalendarCell';
import { TBody, Tr } from './CalendarTable';
import { dates, unitType } from './utils';

/**
 * @description Props for calendar body.
 * @interface IRangeDatePickerCalendarBody
 *
 * @property{calendar Left of Right Date calendar.
 * @property{endDate} The end date of the initially selected date range.
 * @property{maxDate} The latest date a user may select.
 * @property{minDate} The earliest date a user may select.
 * @property{onDayClick} Function - call on cell click.
 * @property{onDayMouseEnter} Function - call on cell mouse enter
 * @property{range} The range between Start and End date.
 * @property{showISOWeekNumbers} Show or not ISO week numbers.
 * @property{showWeekNumbers} Show or not week number on the left side of calendar.
 * @property{side} Left or Right side of calendar.
 * @property{startDate} The beginning date of the initially selected date range.
 */
export interface IRangeDatePickerCalendarBody {
    calendar: Moment;
    endDate: Moment;
    maxDate: Moment;
    minDate: Moment;
    onDayClick: (day: Moment, isLeft: boolean) => void;
    onDayMouseEnter: (day: Moment) => void;
    range: Moment;
    showISOWeekNumbers: boolean;
    showWeekNumbers: boolean;
    side: ECalendarBodySide;
    startDate: Moment;
}

export enum ECalendarBodySide {
    left = 'left',
    right = 'right'
}

/**
 * CalendarBody component.
 *
 * Props: IRangeDatePickerCalendarBody.
 * Render Tbody of the DatePicker.
 * Tbody contains all cells in one month.
 */
export default class CalendarBody extends React.Component<IRangeDatePickerCalendarBody> {

    public renderCells = () => {
        const {
            calendar,
            endDate,
            maxDate,
            minDate,
            onDayClick,
            onDayMouseEnter,
            range,
            showISOWeekNumbers,
            showWeekNumbers,
            side,
            startDate,
        } = this.props;

        /* Define time period */
        const startOfMonth = calendar.clone().startOf('month');
        const startOfWeek = startOfMonth.clone().startOf('isoWeek');
        const endOfMonth = calendar.clone().endOf('month');
        const endOfWeek = endOfMonth.clone().endOf('isoWeek');

        /* Add all days in one month*/
        const data = [];
        let s = startOfWeek.clone();

        while (s <= endOfWeek) {
            data.push(s);
            s = s.clone().add(1, 'day');
        }

        /* Complement array to 42 elements (7 horizontal x 6 vertical cells) */
        if (data.length < 42) {
            let fill = data.length;
            for (; fill < 42; fill++) {
                data.push(s);
                s = s.clone().add(1, 'day');
            }
        }
        const resolvedData = [];

        // Fill each row (week) with day info
        for (let cxi = 0; cxi < 6; cxi++) {
            const rows = [];
            const rxi = cxi * 7;
            const rxiDay = data[rxi];

            if (showISOWeekNumbers || showWeekNumbers) {
                const week = showISOWeekNumbers
                    ? dates.isoWeek(rxiDay.toDate())
                    : rxiDay.week();
                rows.push({
                    key: 0,
                    unitType: unitType.WEEK,
                    week,
                });
            }

            const initLength = rows.length;
            // set props to each day in week row
            for (let cxj = 0; cxj < 7; cxj++) {
                const key = rxi + cxj;
                const day = data[key];
                const props = {
                    calendar,
                    day,
                    endDate,
                    key: key + initLength,
                    maxDate,
                    minDate,
                    onDayClick,
                    onDayMouseEnter,
                    range,
                    side,
                    startDate,
                    unitType: unitType.DAY,
                };
                rows.push(props);
            }
            resolvedData.push(rows);
        }
        return resolvedData.map((rows, rowKey) => {
            const cell = rows.map((props) => {
                const Component = cellMapper[props.unitType];
                // @ts-ignore
                return <Component key={rowKey} {...props} />;

            });
            return <Tr key={rowKey}>{cell}</Tr>;
        });
    };

    public render() {
        return <TBody className="range-date-picker__calendar-body">{this.renderCells()}</TBody>;
    }
}
